import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { CustomDropdown } from '@medprompt/shared';
import MicrophoneCheckService, {
  MicrophoneStatus
} from '../services/MicrophoneCheckService';
import '@medprompt/shared/src/common/dropdowns.css';
import './MicrophoneMonitor.css';

const BAR_COUNT = 20;
const LEVEL_ONLY_BAR_COUNT = 14;
/** RMS at/below this → 0 bars (ignore ambient mic floor). */
const METER_NOISE_FLOOR = 0.02;
/** RMS that lights all bars (conversational speech). */
const METER_FULL_SCALE = 0.18;

/**
 * Teams-style microphone monitor: device picker + live amplitude meter.
 *
 * Modes:
 * - preflight: full picker + meter + soft silence hint (new consultation modal)
 * - levelOnly: subtle meter only; pass `externalLevel` from the recorder
 * - recovery: picker + meter for mid-recording interrupt recovery
 */
const MicrophoneMonitor = ({
  mode = 'preflight',
  active = true,
  externalLevel = null,
  dimmed = false,
  preferredDeviceId = null,
  onReady,
  onLevel,
  onError,
  onDeviceChange,
  showSilenceHint = true
}) => {
  const [devices, setDevices] = useState([]);
  const [deviceId, setDeviceId] = useState(preferredDeviceId || MicrophoneCheckService.getLastDeviceId() || '');
  const [level, setLevel] = useState(0);
  const [errorMessage, setErrorMessage] = useState('');
  const [canRecord, setCanRecord] = useState(false);
  const [hasHeardSignal, setHasHeardSignal] = useState(false);
  const [deviceDropdownOpen, setDeviceDropdownOpen] = useState(false);

  const streamRef = useRef(null);
  const audioContextRef = useRef(null);
  const analyserRef = useRef(null);
  const sourceRef = useRef(null);
  const rafRef = useRef(null);
  const openGenerationRef = useRef(0);
  const deviceDropdownRef = useRef(null);
  const deviceIdRef = useRef(deviceId);
  const onReadyRef = useRef(onReady);
  const onLevelRef = useRef(onLevel);
  const onErrorRef = useRef(onError);
  const onDeviceChangeRef = useRef(onDeviceChange);

  useEffect(() => { deviceIdRef.current = deviceId; }, [deviceId]);
  useEffect(() => { onReadyRef.current = onReady; }, [onReady]);
  useEffect(() => { onLevelRef.current = onLevel; }, [onLevel]);
  useEffect(() => { onErrorRef.current = onError; }, [onError]);
  useEffect(() => { onDeviceChangeRef.current = onDeviceChange; }, [onDeviceChange]);

  const stopMonitorStream = useCallback(() => {
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
    if (sourceRef.current) {
      try { sourceRef.current.disconnect(); } catch { /* ignore */ }
      sourceRef.current = null;
    }
    analyserRef.current = null;
    if (audioContextRef.current) {
      audioContextRef.current.close().catch(() => {});
      audioContextRef.current = null;
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
  }, []);

  const discardStream = useCallback((stream) => {
    if (!stream) return;
    stream.getTracks().forEach((track) => track.stop());
  }, []);

  const refreshDevices = useCallback(async () => {
    try {
      const inputs = await MicrophoneCheckService.enumerateAudioInputs();
      setDevices(inputs);

      const preferred = preferredDeviceId || MicrophoneCheckService.getLastDeviceId();
      const currentStillValid = inputs.some((d) => d.deviceId === deviceIdRef.current);
      if (!currentStillValid) {
        const next = inputs.find((d) => d.deviceId === preferred)?.deviceId
          || inputs[0]?.deviceId
          || '';
        if (next !== deviceIdRef.current) {
          setDeviceId(next);
        }
      }
    } catch (err) {
      console.error('Failed to enumerate audio inputs:', err);
    }
  }, [preferredDeviceId]);

  const startLevelLoop = useCallback(() => {
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
    }

    const buffer = new Uint8Array(analyserRef.current?.fftSize || 2048);

    const tick = () => {
      if (!analyserRef.current) return;
      const rms = MicrophoneCheckService.computeRms(analyserRef.current, buffer);
      setLevel(rms);
      if (rms >= METER_NOISE_FLOOR) {
        setHasHeardSignal(true);
      }
      onLevelRef.current?.(rms);
      rafRef.current = requestAnimationFrame(tick);
    };

    rafRef.current = requestAnimationFrame(tick);
  }, []);

  const openStreamForDevice = useCallback(async (nextDeviceId) => {
    if (mode === 'levelOnly') return;

    const generation = ++openGenerationRef.current;
    stopMonitorStream();
    setErrorMessage('');
    setCanRecord(false);

    if (!MicrophoneCheckService.isMediaDevicesSupported()) {
      const status = {
        status: MicrophoneStatus.UNSUPPORTED,
        message: 'Your browser does not support audio recording.',
        canRecord: false
      };
      setErrorMessage(status.message);
      onErrorRef.current?.(status);
      return;
    }

    try {
      const stream = await MicrophoneCheckService.getUserMediaForDevice(nextDeviceId || null);
      if (generation !== openGenerationRef.current) {
        discardStream(stream);
        return;
      }
      streamRef.current = stream;

      const track = stream.getAudioTracks()[0];
      const actualDeviceId = track?.getSettings?.()?.deviceId || nextDeviceId || '';
      if (actualDeviceId && actualDeviceId !== deviceIdRef.current) {
        setDeviceId(actualDeviceId);
      }
      if (actualDeviceId) {
        MicrophoneCheckService.setLastDeviceId(actualDeviceId);
      }

      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      const analyser = audioContext.createAnalyser();
      analyser.fftSize = 2048;
      analyser.smoothingTimeConstant = 0.8;
      const source = audioContext.createMediaStreamSource(stream);
      source.connect(analyser);

      if (generation !== openGenerationRef.current) {
        source.disconnect();
        audioContext.close().catch(() => {});
        discardStream(stream);
        if (streamRef.current === stream) streamRef.current = null;
        return;
      }

      audioContextRef.current = audioContext;
      analyserRef.current = analyser;
      sourceRef.current = source;

      setCanRecord(true);
      setHasHeardSignal(false);
      startLevelLoop();
      await refreshDevices();

      if (generation !== openGenerationRef.current) {
        stopMonitorStream();
        return;
      }

      onReadyRef.current?.({ deviceId: actualDeviceId, stream, canRecord: true });
    } catch (error) {
      if (generation !== openGenerationRef.current) return;
      const status = MicrophoneCheckService.handleGetUserMediaError(error);
      setErrorMessage(status.message);
      setCanRecord(false);
      onErrorRef.current?.(status);
    }
  }, [mode, refreshDevices, startLevelLoop, stopMonitorStream, discardStream]);

  // External level for levelOnly mode (from AudioRecorderService)
  useEffect(() => {
    if (mode !== 'levelOnly') return;
    if (externalLevel == null) {
      setLevel(0);
      return;
    }
    setLevel(externalLevel);
  }, [mode, externalLevel]);

  // Start / stop based on active + mode
  useEffect(() => {
    if (!active || mode === 'levelOnly') {
      if (mode !== 'levelOnly') {
        openGenerationRef.current += 1;
        stopMonitorStream();
        setCanRecord(false);
        setLevel(0);
        setDeviceDropdownOpen(false);
      }
      return undefined;
    }

    openStreamForDevice(deviceIdRef.current);

    const onDeviceListChange = () => {
      refreshDevices();
    };
    navigator.mediaDevices?.addEventListener?.('devicechange', onDeviceListChange);

    return () => {
      openGenerationRef.current += 1;
      navigator.mediaDevices?.removeEventListener?.('devicechange', onDeviceListChange);
      stopMonitorStream();
      setDeviceDropdownOpen(false);
    };
    // Intentionally only re-run when active/mode flips — device changes handled below
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, mode]);

  const handleDeviceSelect = useCallback((option) => {
    const nextId = option?.id || '';
    setDeviceId(nextId);
    setDeviceDropdownOpen(false);
    onDeviceChangeRef.current?.(nextId);
    if (nextId) {
      MicrophoneCheckService.setLastDeviceId(nextId);
      openStreamForDevice(nextId);
    }
  }, [openStreamForDevice]);

  useEffect(() => {
    if (preferredDeviceId && preferredDeviceId !== deviceIdRef.current && mode !== 'levelOnly') {
      setDeviceId(preferredDeviceId);
    }
  }, [preferredDeviceId, mode]);

  const deviceOptions = useMemo(
    () => devices.map((device, index) => ({
      id: device.deviceId,
      name: device.label || `Microphone ${index + 1}`
    })),
    [devices]
  );

  const selectedDeviceName = useMemo(() => {
    if (devices.length === 0) return 'No microphones found';
    const match = deviceOptions.find((option) => option.id === deviceId);
    return match?.name || 'Select microphone';
  }, [devices.length, deviceOptions, deviceId]);

  const barCount = mode === 'levelOnly' ? LEVEL_ONLY_BAR_COUNT : BAR_COUNT;
  // Floor ambient noise to 0 bars; scale remaining range for speech
  const voiceLevel = level <= METER_NOISE_FLOOR
    ? 0
    : (level - METER_NOISE_FLOOR) / (METER_FULL_SCALE - METER_NOISE_FLOOR);
  const scaled = Math.min(1, Math.max(0, voiceLevel));
  const activeBars = scaled <= 0 ? 0 : Math.max(1, Math.round(scaled * barCount));
  const showPicker = mode === 'preflight' || mode === 'recovery';
  const showSoftSilenceHint = showSilenceHint
    && showPicker
    && canRecord
    && !hasHeardSignal
    && !errorMessage;

  if (mode === 'levelOnly') {
    return (
      <div
        className={`mic-monitor-meter level-only${dimmed ? ' dimmed' : ''}`}
        title="Microphone level"
        aria-label="Microphone level"
      >
        {Array.from({ length: barCount }).map((_, index) => {
          const isOn = index < activeBars;
          return (
            <div
              key={index}
              className={`mic-monitor-bar${isOn ? (scaled > 0.85 ? ' warn' : ' active') : ''}`}
            />
          );
        })}
      </div>
    );
  }

  return (
    <div className={`mic-monitor ${mode}`}>
      {showPicker && (
        <div className="mic-monitor-device-row">
          <label>Microphone</label>
          <CustomDropdown
            selected={selectedDeviceName}
            options={deviceOptions}
            isOpen={deviceDropdownOpen}
            toggleDropdown={() => {
              if (active && devices.length > 0) {
                setDeviceDropdownOpen((open) => !open);
              }
            }}
            onSelect={handleDeviceSelect}
            containerRef={deviceDropdownRef}
            keyExtractor={(option) => option.id}
            disabled={!active || devices.length === 0}
          />
        </div>
      )}

      <div className="mic-monitor-meter-row">
        <span className="mic-monitor-meter-label">Level</span>
        <div className="mic-monitor-meter" aria-label="Microphone level">
          {Array.from({ length: barCount }).map((_, index) => {
            const isOn = index < activeBars;
            return (
              <div
                key={index}
                className={`mic-monitor-bar${isOn ? (scaled > 0.85 ? ' warn' : ' active') : ''}`}
              />
            );
          })}
        </div>
      </div>

      {showSoftSilenceHint && (
        <p className="mic-monitor-hint">
          Speak to confirm the mic is picking up audio.
        </p>
      )}

      {errorMessage && (
        <p className="mic-monitor-error">{errorMessage}</p>
      )}
    </div>
  );
};

export default MicrophoneMonitor;
